import { useContext, useEffect, useRef, useState } from 'react';

import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import vtkGenericRenderWindow from 'vtk.js/Sources/Rendering/Misc/GenericRenderWindow';
import vtkOpenGLRenderWindow from 'vtk.js/Sources/Rendering/OpenGL/RenderWindow';
import vtkRenderer from 'vtk.js/Sources/Rendering/Core/Renderer';
import vtkOrientationMarkerWidget from 'vtk.js/Sources/Interaction/Widgets/OrientationMarkerWidget';

import { colorValues } from '../shared/utils';
import { AppContext } from '../App';
import { ViewOrientation } from '../shared/common';
import { Mesh } from '../model/Mesh';
import { vtkUtil } from '../shared/vtkUtils';
import OcclusionProximityRuler from '../shared/OcclusionProximityRuler';
import occlusionMapping from '../model/OcclusionMapping';
import { OccluUtil } from '../shared/OccluUtil';
import Tr from '../shared/tr';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      height: '100%',
    },
    ruler: {
      position: 'absolute',
      right: 20,
      bottom: 20,
      zIndex: 3,
      pointerEvents: 'none',
    },
  })
);

interface CanvasProps {
  id?: string;
  background?: string;
  hideOrientationMarker?: boolean;
}

interface RenderObject {
  renderWindow: vtkOpenGLRenderWindow;
  renderer: vtkRenderer;
}

const OcclusionProximityCanvas = ({ id, background, hideOrientationMarker }: CanvasProps) => {
  const tr = new Tr();
  const container = useRef(null);
  const classes = useStyles();

  const {
    normalMeshes,
    zoomFit,
    setZoomFit,
    resetCamera,
    setResetCamera,
    viewOrientation,
    setViewOrientation,
    snapshot,
    setSnapshot,
    setSnapshotImage,
    setBusyDlgProp,
  } = useContext(AppContext);

  const [renderObject, setRenderObject] = useState<RenderObject>(null);
  const [orientMarker, setOrientMarker] = useState<vtkOrientationMarkerWidget>();
  const [upMesh, setUpMesh] = useState<Mesh>(null);
  const [lowMesh, setLowMesh] = useState<Mesh>(null);
  const [canDisplayOccluResult, setCanDisplayOccluResult] = useState(false);

  useEffect(() => {
    if (container) {
      initCanvas();
    }
  }, [container]);

  useEffect(() => {
    if (renderObject && !hideOrientationMarker) {
      const marker = vtkUtil.createOrientationMarkerWidget(renderObject.renderWindow.getInteractor());
      setOrientMarker(marker);
    }
  }, [renderObject]);

  useEffect(() => {
    if (zoomFit && renderObject) {
      renderObject.renderer.resetCamera();
      renderObject.renderWindow.render();
      setZoomFit(false);
    }
  }, [zoomFit]);

  useEffect(() => {
    if (resetCamera && renderObject) {
      setZoomFit(true);
      setResetCamera(false);
      setViewOrientation(ViewOrientation.Bottom);
    }
  }, [resetCamera]);

  useEffect(() => {
    if (!renderObject || viewOrientation === ViewOrientation.Unknown) {
      return;
    }

    const cam = renderObject.renderer.getActiveCamera();
    vtkUtil.setCameraOrientation(cam, viewOrientation);

    if (orientMarker) {
      orientMarker.updateMarkerOrientation();
    }
    renderObject.renderer.resetCamera();
    renderObject.renderWindow.render();
    setViewOrientation(ViewOrientation.Unknown);
  }, [viewOrientation]);

  useEffect(() => {
    if (normalMeshes) {
      if (occlusionMapping.hasOccluResult()) {
        setCanDisplayOccluResult(true);
        return;
      }
      normalMeshes.forEach((mesh) => {
        if (upMesh && lowMesh) {
          return;
        }
        if (!upMesh && mesh.isCommonUpperJaw()) {
          setUpMesh(OccluUtil.createOccluMesh(mesh));
          return;
        }
        if (!lowMesh && mesh.isCommonLowerJaw()) {
          setLowMesh(OccluUtil.createOccluMesh(mesh));
          return;
        }
      });
    }
  }, [normalMeshes]);

  useEffect(() => {
    if (upMesh && lowMesh) {
      setBusyDlgProp({ visible: true, title: tr.occlusionCaculating() });
      occlusionMapping.computeOcclusionPressure(upMesh, lowMesh);
      occlusionMapping.setOnProcessSuccess(() => {
        setCanDisplayOccluResult(true);
        setBusyDlgProp({ visible: false, title: '' });
      });
      occlusionMapping.setOnProcessFail(() => {
        // FIXME(Xinyu)
        console.error('occlusion mapping process failed');
      });
    }
  }, [upMesh, lowMesh]);

  // handle resize
  useEffect(() => {
    if (!container || !renderObject) {
      return;
    }
    const dom = container.current;
    const view = renderObject.renderWindow.getInteractor().getView();
    if (!dom || !view) {
      return;
    }
    const sz = view.getSize();
    if (sz[0] != dom.clientWidth || sz[1] != dom.clientHeight) {
      view.setSize(dom.clientWidth, dom.clientHeight);
      renderObject.renderWindow.render();
    }
  });

  // handle refresh
  useEffect(() => {
    window.addEventListener('refreshCanvas', renderCanvas);
    return () => {
      window.removeEventListener('refreshCanvas', renderCanvas);
    };
  });

  useEffect(() => {
    if (snapshot && renderObject) {
      handleSnapshot();
      setSnapshot(false);
    }
  }, [snapshot]);

  useEffect(() => {
    if (renderObject && canDisplayOccluResult) {
      displayOccluResult();
    }
  }, [renderObject, canDisplayOccluResult]);

  // utilities start
  const initCanvas = () => {
    const bgArr = colorValues(background);
    const bgArrF = [bgArr[0] / 255.0, bgArr[1] / 255.0, bgArr[2] / 255.0];
    const genericRenderWindow = vtkGenericRenderWindow.newInstance({
      background: bgArrF,
    });
    genericRenderWindow.setContainer(container.current);
    const renderer = genericRenderWindow.getRenderer();
    const renderWindow = genericRenderWindow.getRenderWindow();
    renderer.resetCamera();
    renderer.updateLightsGeometryToFollowCamera();
    genericRenderWindow.resize();
    setRenderObject({ renderWindow, renderer });
  };

  const addMesh = (mesh: Mesh) => {
    console.log('addMesh', mesh);
    if (mesh && mesh.actor) {
      console.log('addActor', mesh.actor);
      mesh.actor.setVisibility(mesh.visible);
      renderObject.renderer.addActor(mesh.actor);
      renderObject.renderer.resetCamera();
      renderObject.renderWindow.render();
    }
  };

  const renderCanvas = () => {
    if (renderObject) {
      renderObject.renderWindow.render();
    }
  };

  const handleSnapshot = () => {
    const view = renderObject.renderWindow.getInteractor().getView();
    if (view) {
      view.captureNextImage().then((imgSrc) => {
        setSnapshotImage(imgSrc);
      });
      renderObject.renderWindow.render();
    }
  };

  const displayOccluResult = () => {
    const upper = occlusionMapping.getUpperMesh();
    const lower = occlusionMapping.getLowerMesh();
    OccluUtil.spreadUpperandLowerArch(upper.actor, lower.actor);
    OccluUtil.setCameraforSpreadMesh(renderObject.renderer);
    addMesh(upper);
    addMesh(lower);
  };

  // utilities end
  return (
    <div id={id} className={classes.root}>
      <div ref={container} className={classes.root} />
      <div className={classes.ruler}>
        <OcclusionProximityRuler />
      </div>
    </div>
  );
};

export default OcclusionProximityCanvas;
